/**
 * ProcessTimeline - Ask UI redesign Phase A (PR-4) "working" checklist.
 *
 * Renders the lawyer-voice progress steps emitted by the PR-3 backend as SSE
 * `progress` events and accumulated into the STREAMING state by the chat
 * reducer (src/workers/chat/state-machine.ts):
 *
 *   progress?: { step, total, label_vi, label_en, status: 'done' | 'running' }[]
 *   (sorted by step, same-step replaced)
 *
 * This REPLACES the old gold ".asklaw-chat__bubble--thinking" bubble. It uses
 * the PR-1 --asklaw-processing token family (blue "dang lam viec"), NOT gold /
 * warning - the working state is informational, not a caution.
 *
 * Three render shapes:
 *   - steps EMPTY (SENDING phase, before the first progress frame) -> a single
 *     spinner row with a generic "starting" line.
 *   - some step 'running' -> EXPANDED vertical checklist: a green tick for each
 *     'done' step, a CSS spinner for the 'running' step, ordered by step. Sits
 *     in a rounded --asklaw-processing-soft box.
 *   - ALL steps 'done' -> COLLAPSE to one compact muted summary line. This
 *     collapsed summary only shows BRIEFLY during streaming, in the window
 *     where every progress step has reported 'done' but the message has not yet
 *     finalized. It does NOT persist after the answer: in the /chat wiring the
 *     reducer returns to IDLE on `done`, so ProcessTimeline unmounts when the
 *     answer finalizes. Persisting a post-answer summary pinned onto the
 *     finished message is a deliberate Phase A deferral (not implemented).
 *
 * A11y: container role="status" aria-live="polite" so SR announce step flips;
 * spinners are aria-hidden. prefers-reduced-motion disables the spin animation
 * (static ring). VN copy carries full diacritics (user-facing, F-FE-CHAT AC-21).
 *
 * Styling follows the CitationSourcesPanel pattern for the Ask flow: className
 * hooks + a co-located <style> block (NOT chat.css), so the lightweight /ask
 * route does not pull the large chat.css. Design tokens only - no hard-coded
 * colors, no new npm deps (inline SVG check + CSS spinner).
 */

import { useEffect, useState, type ReactElement } from 'react';

export interface ProcessTimelineStep {
  step: number;
  total: number;
  label_vi: string;
  label_en: string;
  status: 'done' | 'running';
}

export interface ProcessTimelineProps {
  steps: ProcessTimelineStep[];
  lang: 'vi' | 'en';
}

const COPY = {
  starting: { vi: 'Đang xử lý câu hỏi của bạn...', en: 'Processing your question...' },
  summary: {
    vi: 'Đã tra cứu xong văn bản pháp luật',
    en: 'Finished researching the legal sources',
  },
  // aria label for the whole live region
  region: { vi: 'Tiến trình xử lý câu hỏi', en: 'Question processing progress' },
} as const;

/**
 * Issue 5: CLIENT-SIDE progressive "thinking" reveal for the SENDING phase
 * (steps empty, before the backend's first buffered `progress` frame arrives).
 *
 * These are GENERIC phase labels, deliberately NOT mapped 1-1 with real backend
 * timing (per founder "khong can mapping 1-1") - they just make the dead time
 * feel like a reasoning process unfolding instead of a single static spinner.
 * They make NO factual claim (no "tim thay N nguon" counts - those are backend
 * truth surfaced only by the real `sources` event). Once real progress events
 * arrive (steps non-empty) this preview is dropped and the real steps take over.
 *
 * Exported so a unit test can assert the labels exist + carry VN diacritics
 * (the timed reveal itself runs in an effect, not in renderToStaticMarkup).
 */
export const PREVIEW_STEPS: { vi: string; en: string }[] = [
  { vi: 'Đang đọc câu hỏi của bạn...', en: 'Reading your question...' },
  {
    vi: 'Đang tra cứu văn bản pháp luật liên quan...',
    en: 'Searching the relevant legal sources...',
  },
  { vi: 'Đang đối chiếu các quy định...', en: 'Matching the applicable provisions...' },
];

/** Delay between each preview step reveal (ms). Generic pacing, not backend-tied. */
const PREVIEW_REVEAL_MS = 800;

/* Scoped styles, co-located (matches CitationSourcesPanel). Tokens are defined
   in global.css with light + dark variants, so no per-component dark override
   is needed here. */
const TIMELINE_CSS = `
.asklaw-process-timeline {
  font-family: var(--font-sans, system-ui, sans-serif);
  box-sizing: border-box;
}
.asklaw-process-timeline *,
.asklaw-process-timeline *::before,
.asklaw-process-timeline *::after { box-sizing: border-box; }

/* Expanded "working" box. */
.asklaw-process-timeline--expanded {
  display: flex;
  flex-direction: column;
  gap: var(--space-2, 8px);
  max-width: 520px;
  padding: var(--space-3, 12px) var(--space-4, 16px);
  background: var(--asklaw-processing-soft);
  border: 1px solid color-mix(in srgb, var(--asklaw-processing) 32%, transparent);
  border-radius: var(--radius-lg, 12px);
}
.asklaw-process-timeline__row {
  display: flex;
  align-items: center;
  gap: var(--space-2, 8px);
  font-size: 14px;
  line-height: 1.4;
  color: var(--asklaw-ink);
}
.asklaw-process-timeline__icon {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.asklaw-process-timeline__check { color: var(--asklaw-success); }
.asklaw-process-timeline__label { min-width: 0; }
.asklaw-process-timeline__row--running .asklaw-process-timeline__label {
  font-style: italic;
  color: var(--asklaw-ink);
  animation: asklaw-process-pulse 1.4s ease-in-out infinite;
}

/* CSS spinner ring (running step + starting state). */
.asklaw-process-spinner {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid color-mix(in srgb, var(--asklaw-processing) 30%, transparent);
  border-top-color: var(--asklaw-processing);
  animation: asklaw-process-spin 0.8s linear infinite;
}

/* Generic starting row (steps empty / SENDING). */
.asklaw-process-timeline--starting {
  display: flex;
  align-items: center;
  gap: var(--space-2, 8px);
  max-width: 520px;
  padding: var(--space-3, 12px) var(--space-4, 16px);
  background: var(--asklaw-processing-soft);
  border: 1px solid color-mix(in srgb, var(--asklaw-processing) 32%, transparent);
  border-radius: var(--radius-lg, 12px);
  font-size: 14px;
  line-height: 1.4;
  color: var(--asklaw-ink);
}

/* Collapsed summary line (all done). */
.asklaw-process-timeline--summary {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2, 8px);
  font-size: 13px;
  line-height: 1.4;
  color: var(--asklaw-muted);
}
.asklaw-process-timeline--summary .asklaw-process-timeline__check {
  color: var(--asklaw-success);
}

@keyframes asklaw-process-spin { to { transform: rotate(360deg); } }
@keyframes asklaw-process-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
}

@media (prefers-reduced-motion: reduce) {
  .asklaw-process-spinner { animation: none; }
  .asklaw-process-timeline__row--running .asklaw-process-timeline__label { animation: none; }
}
`;

function CheckIcon(): ReactElement {
  return (
    <svg
      className="asklaw-process-timeline__icon asklaw-process-timeline__check"
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function Spinner(): ReactElement {
  return <span className="asklaw-process-spinner" aria-hidden="true" />;
}

export function ProcessTimeline({ steps, lang }: ProcessTimelineProps): ReactElement {
  const regionLabel = COPY.region[lang];

  const stepsEmpty = !steps || steps.length === 0;

  // Issue 5: while in the SENDING window (steps empty) advance an internal
  // "previewCount" 0 -> 1 -> 2 -> 3 on a timer so the generic reasoning steps
  // reveal one by one. Starts at 0 (server render / first paint shows the plain
  // starting spinner, keeping behavior + tests stable); the browser effect then
  // reveals PREVIEW_STEPS[0..2]. Cleared on unmount AND the moment real progress
  // arrives (stepsEmpty flips false) so the real steps take over cleanly.
  const [previewCount, setPreviewCount] = useState(0);

  useEffect(() => {
    if (!stepsEmpty) {
      // Real progress arrived -> stop the preview (reset for any next question).
      setPreviewCount(0);
      return;
    }
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 1; i <= PREVIEW_STEPS.length; i += 1) {
      timers.push(
        setTimeout(() => setPreviewCount(i), PREVIEW_REVEAL_MS * i),
      );
    }
    return () => {
      for (const t of timers) clearTimeout(t);
    };
  }, [stepsEmpty]);

  // 1) Empty -> generic starting state (SENDING, before first progress frame).
  if (stepsEmpty) {
    // 1a) Before the first preview reveal (previewCount 0): the original plain
    // starting spinner + "Đang xử lý..." line (also what SSR / tests render).
    if (previewCount <= 0) {
      return (
        <div
          className="asklaw-process-timeline"
          role="status"
          aria-live="polite"
          aria-label={regionLabel}
        >
          <style>{TIMELINE_CSS}</style>
          <div className="asklaw-process-timeline--starting">
            <Spinner />
            <span className="asklaw-process-timeline__label">{COPY.starting[lang]}</span>
          </div>
        </div>
      );
    }

    // 1b) Progressive reveal: render the first `previewCount` generic phase
    // lines as a checklist - earlier ones get a tick, the latest gets a spinner
    // (the "currently thinking about" line). Honest in-progress phrasing, no
    // factual counts. When the real backend `progress` events land this branch
    // is replaced by the real expanded checklist below.
    const shown = PREVIEW_STEPS.slice(0, previewCount);
    return (
      <div
        className="asklaw-process-timeline"
        role="status"
        aria-live="polite"
        aria-label={regionLabel}
      >
        <style>{TIMELINE_CSS}</style>
        <div className="asklaw-process-timeline--expanded">
          {shown.map((p, i) => {
            const running = i === shown.length - 1;
            const label = lang === 'vi' ? p.vi : p.en;
            return (
              <div
                key={i}
                className={
                  running
                    ? 'asklaw-process-timeline__row asklaw-process-timeline__row--running'
                    : 'asklaw-process-timeline__row'
                }
              >
                <span className="asklaw-process-timeline__icon">
                  {running ? <Spinner /> : <CheckIcon />}
                </span>
                <span className="asklaw-process-timeline__label">{label}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const ordered = [...steps].sort((a, b) => a.step - b.step);
  const allDone = ordered.every((s) => s.status === 'done');

  // 3) All done -> collapsed compact summary line.
  if (allDone) {
    return (
      <div
        className="asklaw-process-timeline"
        role="status"
        aria-live="polite"
        aria-label={regionLabel}
      >
        <style>{TIMELINE_CSS}</style>
        <div className="asklaw-process-timeline--summary">
          <CheckIcon />
          <span className="asklaw-process-timeline__label">{COPY.summary[lang]}</span>
        </div>
      </div>
    );
  }

  // 2) Some running -> expanded vertical checklist.
  return (
    <div
      className="asklaw-process-timeline"
      role="status"
      aria-live="polite"
      aria-label={regionLabel}
    >
      <style>{TIMELINE_CSS}</style>
      <div className="asklaw-process-timeline--expanded">
        {ordered.map((s) => {
          const label = lang === 'vi' ? s.label_vi : s.label_en;
          const running = s.status === 'running';
          return (
            <div
              key={s.step}
              className={
                running
                  ? 'asklaw-process-timeline__row asklaw-process-timeline__row--running'
                  : 'asklaw-process-timeline__row'
              }
            >
              <span className="asklaw-process-timeline__icon">
                {running ? <Spinner /> : <CheckIcon />}
              </span>
              <span className="asklaw-process-timeline__label">{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ProcessTimeline;
